/**
 * Houseplant v2 — Feature cards slider (mobile only)
 *
 * Drives sections/hp-feature-cards.liquid below 750, where node 9120:4859 turns
 * the desktop 3-column grid into one card per view with bar pagination. Above
 * 750 the same markup is a grid, the dots are hidden, and nothing here does
 * anything: a grid is not a scroll container, so the scroll listener never
 * fires, and a hidden bar can't be clicked.
 *
 * The track is a native scroll-snap container, so swipe, momentum and snapping
 * are the browser's job. This owns only the bars: click to go, and stay in sync
 * when the visitor swipes.
 *
 * ── SECOND USE OF THE BAR-PAGINATION CONTRACT ────────────────────────────────
 * sections/hp-hero-banner.liquid does the same thing in hp-hero-slider.js, with
 * auto-rotation and theme-editor plumbing layered on top. The shared part is
 * exactly what's below: dots → goTo, scroll → setActive, `is-active` +
 * aria-current. Per CLAUDE.md this is the SECOND use, so it gets this note
 * rather than an extraction; a third consumer should lift this file into a base
 * class and leave the hero's autoplay as its own subclass. The dot CSS carries
 * the matching note.
 *
 * NO AUTOPLAY here, deliberately — the node has none, and a section the visitor
 * reads rather than watches shouldn't move under them.
 */
class HpFeatureSlider extends HTMLElement {
  connectedCallback() {
    /* STACK MODE HAS NO SLIDER. The element still wraps the list (one markup for
       both layouts), but in a stack there is no scroll container, no snapping
       and no dots — so everything below would bind listeners to a track that
       never scrolls. Bailing here rather than not rendering the element keeps
       the Liquid free of a conditional wrapper tag. */
    if (this.closest('.hp-feature--stack')) return;

    this.track = this.querySelector('.hp-feature__grid');
    this.slides = Array.from(this.querySelectorAll('.hp-feature__item'));
    this.dots = Array.from(this.querySelectorAll('.hp-feature__dot'));

    if (!this.track || this.slides.length < 2) return;

    this.index = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onScroll = this.onScroll.bind(this);

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goTo(Number(dot.dataset.index)));
    });

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
  }

  goTo(index) {
    const slide = this.slides[index];
    if (!slide) return;

    this.track.scrollTo({
      left: slide.offsetLeft - this.track.offsetLeft,
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
    });
    this.setActive(index);
  }

  setActive(index) {
    if (index === this.index) return;
    this.index = index;

    this.dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('is-active', isActive);
      // Removed rather than set to "false": aria-current="false" is still an
      // announced state in some screen readers.
      if (isActive) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  /**
   * Scroll position → active slide. Read inside rAF so a momentum scroll doesn't
   * queue a layout read per frame.
   */
  onScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;

      const origin = this.track.scrollLeft + this.track.offsetLeft;
      const nearest = this.slides.reduce((best, slide, i) => {
        const distance = Math.abs(slide.offsetLeft - origin);
        return !best || distance < best.distance ? { i, distance } : best;
      }, null);

      if (nearest) this.setActive(nearest.i);
    });
  }
}

if (!customElements.get('hp-feature-slider')) {
  customElements.define('hp-feature-slider', HpFeatureSlider);
}

/**
 * Theme editor: selecting a card block should bring it into view on mobile.
 * Harmless on desktop, where `goTo` scrolls a non-scrolling grid by zero.
 */
document.addEventListener('shopify:block:select', (event) => {
  const slider = event.target.closest?.('.hp-feature')?.querySelector('hp-feature-slider');
  if (!slider) return;
  slider.goTo(Number(event.target.dataset.index) || 0);
});

/* ── "View More" ─────────────────────────────────────────────────────────────
 * Reveals the cards the mobile stack caps off (node 9120:7442). One press shows
 * everything and removes the button — the node draws no "View Less", and an
 * archive that can re-collapse under you is worse than a long page.
 *
 * Delegated from the document so it survives a Section Rendering swap and needs
 * no per-section wiring. The attribute it clears is only consulted by a CSS rule
 * inside the mobile media query, so this is inert at ≥750 even if pressed.
 */
document.addEventListener('click', function (event) {
  var button = event.target.closest('[data-hp-feature-more]');
  if (!button) return;
  var section = button.closest('.hp-feature');
  if (!section) return;
  section.querySelectorAll('[data-hp-more-hidden]').forEach(function (item) {
    item.removeAttribute('data-hp-more-hidden');
  });
  var wrapper = button.closest('.hp-feature__more');
  if (wrapper) wrapper.remove();
});
